import { useEffect, useMemo, useState } from 'react';
import { useCanvasStore, dispatch } from '../../store/canvas-store';
import { bundledBrands, type Brand } from '../../style/library';
import { refreshBrandIndex } from '../../style/library/fetchBrandIndex';
import { loadThemeFromLibrary } from '../../style/library/fetchTheme';

interface Props {
  /** Frame to assign the loaded theme to (undefined -> just add to registry). */
  targetFrameId?: string;
  onClose: () => void;
}

export function ThemeLibraryModal({ targetFrameId, onClose }: Props) {
  const themes = useCanvasStore((s) => s.doc.themes) ?? [];
  const [brands, setBrands] = useState<Brand[]>(() => bundledBrands());
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null); // slug currently loading
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.name.toLowerCase().includes(q) || b.slug.toLowerCase().includes(q));
  }, [brands, query]);

  async function onRefresh() {
    setRefreshing(true);
    setError(null);
    const next = await refreshBrandIndex();
    setBrands(next);
    if (next === bundledBrands()) setError("Couldn't refresh — showing bundled list.");
    setRefreshing(false);
  }

  async function onPick(brand: Brand) {
    setBusy(brand.slug);
    setError(null);
    try {
      const theme = await loadThemeFromLibrary(brand.slug);
      const store = useCanvasStore.getState();
      store.beginTransaction('load theme from library');
      const exists = (store.doc.themes ?? []).some((t) => t.id === theme.id);
      dispatch(exists ? { t: 'theme.update', id: theme.id, patch: theme } : { t: 'theme.create', theme });
      if (targetFrameId) dispatch({ t: 'frame.setTheme', id: targetFrameId, themeId: theme.id });
      store.endTransaction();
      onClose();
    } catch (e) {
      setError(`Failed to load ${brand.name}: ${(e as Error).message}`);
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onMouseDown={onClose}
    >
      <div
        className="flex flex-col rounded-lg overflow-hidden"
        style={{
          width: 'min(560px, 90vw)', maxHeight: '80vh',
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-2 px-3 py-2 text-sm"
          style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text)' }}
        >
          <span className="font-medium">Theme Library</span>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brands…"
            className="flex-1 rounded px-2 py-1 text-xs"
            style={{ background: 'var(--color-input-bg)', border: '1px solid var(--color-input-border)', color: 'var(--color-text)' }}
          />
          <button
            type="button" onClick={onRefresh} disabled={refreshing}
            className="px-2 py-1 rounded text-xs"
            style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}
          >
            {refreshing ? '…' : 'Refresh'}
          </button>
          <button type="button" onClick={onClose} className="px-1.5 text-base leading-none" style={{ color: 'var(--color-text-muted)' }}>×</button>
        </div>

        {error && (
          <div className="px-3 py-1.5 text-xs" style={{ color: 'var(--color-warning)' }}>{error}</div>
        )}

        <div className="overflow-auto p-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6 }}>
          {filtered.map((b) => {
            const installed = themes.some((t) => t.id === b.slug || t.name === b.name);
            return (
              <button
                key={b.slug}
                type="button"
                onClick={() => onPick(b)}
                disabled={busy !== null}
                title={b.slug}
                className="text-left px-2.5 py-2 rounded text-xs truncate"
                style={{
                  background: 'var(--color-surface-2)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text)',
                  opacity: busy && busy !== b.slug ? 0.5 : 1,
                }}
              >
                {busy === b.slug ? 'Loading…' : b.name}
                {installed && <span style={{ color: 'var(--color-text-muted)' }}> ✓</span>}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-xs italic p-2" style={{ color: 'var(--color-text-muted)' }}>No matches.</div>
          )}
        </div>

        <div className="px-3 py-1.5 text-xs" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
          Themes from <span className="font-mono">VoltAgent/awesome-design-md</span>
        </div>
      </div>
    </div>
  );
}
